import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { DateInput } from '../../../../shared/components/DateInput.js';

/**
 * M19 L19.04 — Maintenance window governance.
 *
 * Lists maintenance windows from `GET /v1/platform/maintenance-windows`, allows
 * scheduling a new window (`POST`) and driving its lifecycle
 * (`POST :id/start | :id/complete | :id/cancel`).
 */
interface MaintenanceWindowRow {
  readonly maintenanceWindowId: string;
  readonly title: string;
  readonly windowType: string;
  readonly status: string;
  readonly scheduledStart: string;
  readonly scheduledEnd: string;
  readonly bannerMessage?: string | null;
}

const WINDOW_TYPES = ['SCHEDULED', 'EMERGENCY'] as const;

function statusColor(status: string): string {
  const s = status.toUpperCase();
  if (s === 'IN_PROGRESS') return 'var(--color-warning)';
  if (s === 'COMPLETED') return 'var(--color-success)';
  if (s === 'CANCELLED') return 'var(--color-danger)';
  return 'var(--color-text-link)';
}

export function MaintenanceWindowsPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [rows, setRows] = useState<MaintenanceWindowRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [windowType, setWindowType] = useState<string>('SCHEDULED');
  const [scheduledStart, setScheduledStart] = useState('');
  const [scheduledEnd, setScheduledEnd] = useState('');
  const [bannerMessage, setBannerMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    let alive = true;
    apiRequest<{ data: MaintenanceWindowRow[] }>('/v1/platform/maintenance-windows', { token })
      .then((r) => { if (alive) setRows(r.data ?? []); })
      .catch((e) => { if (alive) { setError((e as Error).message); setRows([]); } });
    return () => { alive = false; };
  }, [token]);

  useEffect(() => load(), [load]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!title.trim() || !scheduledStart.trim() || !scheduledEnd.trim()) {
      setError(tr('m19.maintenance.validationRequired'));
      return;
    }
    setSubmitting(true);
    try {
      await apiRequest('/v1/platform/maintenance-windows', {
        method: 'POST',
        token,
        body: {
          title: title.trim(),
          windowType,
          scheduledStart: new Date(scheduledStart).toISOString(),
          scheduledEnd: new Date(scheduledEnd).toISOString(),
          bannerMessage: bannerMessage.trim() || undefined,
        },
      });
      setTitle(''); setScheduledStart(''); setScheduledEnd(''); setBannerMessage('');
      setWindowType('SCHEDULED');
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const act = async (id: string, action: 'start' | 'complete' | 'cancel') => {
    setError(null);
    setBusyId(id);
    try {
      const body = action === 'cancel' ? { reason: 'Cancelled by administrator' } : undefined;
      await apiRequest(`/v1/platform/maintenance-windows/${id}/${action}`, { method: 'POST', token, body });
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const inputStyle = { padding: '6px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' } as const;

  return (
    <main aria-label="maintenance-windows">
      <h1 style={{ fontSize: 20, marginBottom: 4 }}>{tr('m19.maintenance.title')}</h1>
      <p style={{ color: 'var(--color-text-muted)', marginBottom: 16, fontSize: 13 }}>{tr('m19.maintenance.subtitle')}</p>

      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}

      <form aria-label="maintenance-window-form" onSubmit={submit}
            style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 24, maxWidth: 900 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, flex: '1 1 220px' }}>
          {tr('m19.maintenance.labelTitle')}
          <input aria-label="title" value={title} onChange={(e) => setTitle(e.target.value)} style={inputStyle} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          {tr('m19.maintenance.labelType')}
          <select aria-label="window-type" value={windowType} onChange={(e) => setWindowType(e.target.value)} style={inputStyle}>
            {WINDOW_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          {tr('m19.maintenance.labelStart')}
          <DateInput aria-label="scheduled-start" type="datetime-local" value={scheduledStart} onValueChange={(nextValue) => setScheduledStart(nextValue)} style={inputStyle} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          {tr('m19.maintenance.labelEnd')}
          <DateInput aria-label="scheduled-end" type="datetime-local" value={scheduledEnd} onValueChange={(nextValue) => setScheduledEnd(nextValue)} style={inputStyle} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, flex: '1 1 220px' }}>
          {tr('m19.maintenance.labelBanner')}
          <input aria-label="banner-message" value={bannerMessage} onChange={(e) => setBannerMessage(e.target.value)} style={inputStyle} />
        </label>
        <button type="submit" data-testid="submit-window" disabled={submitting}
                style={{ padding: '8px 16px', borderRadius: 4, border: '1px solid var(--color-brand-primary)', background: 'var(--color-brand-primary)', color: 'var(--color-text-inverse)' }}>
          {submitting ? tr('m19.maintenance.scheduling') : tr('m19.maintenance.schedule')}
        </button>
      </form>

      {!rows ? (
        <p>{tr('m19.maintenance.loading')}</p>
      ) : rows.length === 0 ? (
        <p data-testid="windows-empty" style={{ color: 'var(--color-text-muted)' }}>{tr('m19.maintenance.empty')}</p>
      ) : (
        <table aria-label="maintenance-windows-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '2px solid var(--color-border-default)' }}>
              <th style={{ padding: 8 }}>{tr('m19.maintenance.colTitle')}</th>
              <th style={{ padding: 8 }}>{tr('m19.maintenance.colType')}</th>
              <th style={{ padding: 8 }}>{tr('m19.maintenance.colStatus')}</th>
              <th style={{ padding: 8 }}>{tr('m19.maintenance.colStart')}</th>
              <th style={{ padding: 8 }}>{tr('m19.maintenance.colEnd')}</th>
              <th style={{ padding: 8 }}>{tr('m19.maintenance.colActions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((w) => {
              const status = w.status.toUpperCase();
              const disabled = busyId === w.maintenanceWindowId;
              return (
                <tr key={w.maintenanceWindowId} data-testid={`window-row-${w.maintenanceWindowId}`}
                    style={{ borderBottom: '1px solid var(--color-bg-muted)' }}>
                  <td style={{ padding: 8 }}>{w.title}</td>
                  <td style={{ padding: 8 }}>{w.windowType}</td>
                  <td style={{ padding: 8, color: statusColor(w.status), fontWeight: 600 }}>{w.status}</td>
                  <td style={{ padding: 8 }}>{at(w.scheduledStart)}</td>
                  <td style={{ padding: 8 }}>{at(w.scheduledEnd)}</td>
                  <td style={{ padding: 8, display: 'flex', gap: 6 }}>
                    {status === 'SCHEDULED' && (
                      <>
                        <button data-testid={`start-${w.maintenanceWindowId}`} disabled={disabled}
                                onClick={() => act(w.maintenanceWindowId, 'start')}
                                style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid var(--color-success)', background: 'var(--color-success)', color: 'var(--color-text-inverse)' }}>{tr('m19.maintenance.start')}</button>
                        <button data-testid={`cancel-${w.maintenanceWindowId}`} disabled={disabled}
                                onClick={() => act(w.maintenanceWindowId, 'cancel')}
                                style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid var(--color-danger)', background: 'var(--color-bg-surface)', color: 'var(--color-danger)' }}>{tr('m19.maintenance.cancel')}</button>
                      </>
                    )}
                    {status === 'IN_PROGRESS' && (
                      <button data-testid={`complete-${w.maintenanceWindowId}`} disabled={disabled}
                              onClick={() => act(w.maintenanceWindowId, 'complete')}
                              style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid var(--color-success)', background: 'var(--color-success)', color: 'var(--color-text-inverse)' }}>{tr('m19.maintenance.complete')}</button>
                    )}
                    {(status === 'COMPLETED' || status === 'CANCELLED') && (
                      <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </main>
  );
}
